import { createSlice } from '@reduxjs/toolkit';
import { ContextType } from '../../enums/ContextType';
import { SearchAgent } from '../../interfaces/agent';
import Invitation, {
  AgentIdToInvitationCreationErrorMap,
  AgentIdToInvitationPayloadMap,
  InvitationCreationError,
  SearchAgentInvitationMap,
} from '../../interfaces/invitation';
import Search, { SearchAgentMetricsMap } from '../../interfaces/search';
import {
  makeSearchAgentInvitationMapFromInvitationList,
  makeSearchAgentMetricsMapFromSearch,
} from '../../utils/helpers';
import {
  getContextForDeal,
  getDefaultEmailTemplateForDeal,
  getDefaultSmsTemplateForDeal,
  renderDjangoTemplate,
} from '../../utils/templateHelpers';
import { getTemplates } from '../templates/actions';
import {
  cancelInvitationById,
  endSearchActivity,
  fetchSearchById,
  patchDealById,
  patchSearchById,
  postInvitation,
} from './actions';

interface SearchesState {
  hasErrors?: boolean | undefined;
  error?: string | null | undefined;
  loading?: boolean | undefined;
  search: Search | any;
  showSearch: boolean;
  refreshSearch: boolean;
  stagedAgents: number[];
  listingAddress: string;
  lastListingAddress: string;
  successfullyEndedActivity: boolean;
  patchErrors: any;
  searchAgentMetricsMap: SearchAgentMetricsMap;
  searchAgentInvitationMap: SearchAgentInvitationMap;
  stagedInvitationPayloads: AgentIdToInvitationPayloadMap;
  invitationCreationErrors: AgentIdToInvitationCreationErrorMap;
  latestLiveUpdate: number;
  templates: any;
}

const initialState: SearchesState = {
  hasErrors: false,
  error: null,
  loading: false,
  search: {} as Search,
  showSearch: false,
  refreshSearch: false,
  stagedAgents: [],
  listingAddress: '',
  lastListingAddress: '',
  successfullyEndedActivity: false,
  patchErrors: null,
  searchAgentMetricsMap: {} as SearchAgentMetricsMap,
  searchAgentInvitationMap: {} as SearchAgentInvitationMap,
  stagedInvitationPayloads: {} as AgentIdToInvitationPayloadMap,
  invitationCreationErrors: {} as AgentIdToInvitationCreationErrorMap,
  latestLiveUpdate: 0,
  templates: null,
};

const templateResolutionErrorMessage =
  'Deal loaded, but AMP could not build invitation templates. Please check template configuration.';

const setTemplateResolutionError = (state: SearchesState, error: unknown) => {
  const detail = error instanceof Error ? ` ${error.message}` : '';
  const message = `${templateResolutionErrorMessage}${detail}`;

  state.hasErrors = true;
  state.error = message;
  state.patchErrors = { message };
  state.stagedInvitationPayloads = {};
};

const hasTemplateResolutionError = (state: SearchesState) =>
  state.patchErrors?.message?.startsWith(templateResolutionErrorMessage) ||
  state.error?.startsWith(templateResolutionErrorMessage);

const buildStagedInvitationPayloads = (
  payload: Search,
  templates: any,
  searchAgentInvitationMap: SearchAgentInvitationMap,
) => {
  // Templates load independently of the search, so absence is a not-ready state, not an error.
  if (!templates) {
    return {} as AgentIdToInvitationPayloadMap;
  }

  const defaultEmailTemplate = getDefaultEmailTemplateForDeal(
    payload.deal,
    templates,
  );
  const defaultSmsTemplate = getDefaultSmsTemplateForDeal(
    payload.deal,
    templates,
  );

  return payload.opted_in_agents.reduce(
    (acc: AgentIdToInvitationPayloadMap, searchAgent: SearchAgent) => {
      if (
        searchAgent.delay !== null &&
        !searchAgentInvitationMap[searchAgent.agent.vid]
      ) {
        const emailTemplate = defaultEmailTemplate?.value?.template;
        const smsTemplate = defaultSmsTemplate?.value?.template;

        if (emailTemplate === null || emailTemplate === undefined) {
          throw new Error('Missing default email intro template.');
        }
        if (smsTemplate === null || smsTemplate === undefined) {
          throw new Error('Missing default SMS intro template.');
        }

        const customAgentCommission =
          payload.deal.commission_model_name === 'Clever Cash Back (0.5%)'
            ? 40
            : null;
        acc[searchAgent.agent.vid] = {
          agent: searchAgent.agent.vid,
          delay_time: searchAgent.delay,
          search: payload.id,
          email_intro_message: renderDjangoTemplate(
            emailTemplate,
            getContextForDeal(
              ContextType.email,
              payload.deal,
              templates,
              searchAgent.agent,
            ),
          ),
          sms_intro_message: renderDjangoTemplate(
            smsTemplate,
            getContextForDeal(
              ContextType.sms,
              payload.deal,
              templates,
              searchAgent.agent,
            ),
          ),
          custom_agent_commission: customAgentCommission,
        };
      }
      return acc;
    },
    {} as AgentIdToInvitationPayloadMap,
  );
};

const searchesSlice = createSlice({
  name: 'searches',
  initialState,
  reducers: {
    resetEndActivityState(state) {
      state.successfullyEndedActivity = false;
    },
    clearSearchId(state) {
      state.search = {} as Search;
      state.searchAgentInvitationMap = {};
      state.stagedAgents = [];
      state.hasErrors = false;
      state.showSearch = false;
      state.refreshSearch = false;
      state.successfullyEndedActivity = false;
    },
    clearSearchErrors(state) {
      state.hasErrors = false;
      state.patchErrors = null;
    },
    updateShowSearch(state, action) {
      state.refreshSearch = action.payload;
    },
    stageAgentOnSearch(state, action) {
      state.stagedAgents = [...state.stagedAgents, action.payload];
    },
    unstageAgentOnSearch(state, action) {
      state.stagedAgents = state.stagedAgents.filter(
        (stagedAgentId) => stagedAgentId !== action.payload,
      );
    },
    stageInvitationPayload(state, action) {
      state.stagedInvitationPayloads = {
        ...state.stagedInvitationPayloads,
        [action.payload.agent]: action.payload,
      };
    },
    unstageInvitationPayload(state, action) {
      const newStagedInvitationPayloads = { ...state.stagedInvitationPayloads };
      delete newStagedInvitationPayloads[action.payload];
      state.stagedInvitationPayloads = newStagedInvitationPayloads;

      const newInvitationCreationErrors = { ...state.invitationCreationErrors };
      delete newInvitationCreationErrors[action.payload];
      state.invitationCreationErrors = newInvitationCreationErrors;
    },
  },
  extraReducers: (builder) => {
    // Fetch Customer Deals
    builder.addCase(fetchSearchById.pending, (state) => {
      state.loading = true;
      state.patchErrors = null;
    });
    builder.addCase(fetchSearchById.fulfilled, (state, { payload }) => {
      const { templates } = state;

      state.search = payload;
      state.stagedAgents = payload.opted_in_agents.map(
        (searchAgent: SearchAgent) => searchAgent.agent.vid,
      );
      const newSearchAgentInvitationMap =
        makeSearchAgentInvitationMapFromInvitationList(payload.invitations);
      state.searchAgentInvitationMap = newSearchAgentInvitationMap;
      state.searchAgentMetricsMap =
        makeSearchAgentMetricsMapFromSearch(payload);
      state.showSearch = true;
      state.listingAddress = payload.deal?.location
        ? payload.deal?.location?.address
        : '';
      state.loading = false;
      state.hasErrors = false;
      state.error = null;
      state.patchErrors = null;
      try {
        state.stagedInvitationPayloads = buildStagedInvitationPayloads(
          payload,
          templates,
          newSearchAgentInvitationMap,
        );
      } catch (error) {
        setTemplateResolutionError(state, error);
      }
    });
    builder.addCase(fetchSearchById.rejected, (state, { payload }) => {
      state.loading = false;
      state.hasErrors = true; // TODO: add errors
      state.patchErrors = (payload as any)?.errorData || {};
    });
    // Update Customer Deals
    builder.addCase(patchSearchById.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(patchSearchById.fulfilled, (state, { payload }) => {
      state.loading = false;
      const { templates } = state;
      const defaultPlaybook = state.search.default_playbook || undefined;
      state.search = {
        ...payload,
        default_playbook: defaultPlaybook,
      };
      const newSearchAgentInvitationMap =
        makeSearchAgentInvitationMapFromInvitationList(payload.invitations);
      state.searchAgentInvitationMap = newSearchAgentInvitationMap;
      state.searchAgentMetricsMap =
        makeSearchAgentMetricsMapFromSearch(payload);
      state.stagedAgents = payload.opted_in_agents.map(
        (searchAgent: SearchAgent) => searchAgent.agent.vid,
      );
      state.hasErrors = false;
      state.patchErrors = null;
      state.error = null;
      try {
        state.stagedInvitationPayloads = buildStagedInvitationPayloads(
          payload,
          templates,
          newSearchAgentInvitationMap,
        );
      } catch (error) {
        setTemplateResolutionError(state, error);
      }
    });
    builder.addCase(patchSearchById.rejected, (state, { payload }) => {
      state.loading = false;
      state.hasErrors = true;
      state.patchErrors = (payload as any)?.errorData || {};
    });
    // Patch Deal on search
    builder.addCase(patchDealById.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(patchDealById.fulfilled, (state, { payload }) => {
      state.loading = false;
      if (payload.error) {
        state.hasErrors = true;
        state.error =
          'There was an error updating the search/deal. Please check your fields and try again.';
      } else {
        state.search = {
          ...state.search,
          deal: { ...state.search.deal, ...payload },
        };
        state.hasErrors = false;
        state.error = null;
      }
    });
    builder.addCase(patchDealById.rejected, (state) => {
      state.loading = false;
      state.hasErrors = true; // TODO: add errors
    });

    // End Search Activity
    // Reset on pending so a stale fulfilled from an earlier clearAfter=false swap can't fire consumers.
    builder.addCase(endSearchActivity.pending, (state) => {
      state.loading = true;
      state.successfullyEndedActivity = false;
    });

    builder.addCase(endSearchActivity.fulfilled, (state) => {
      state.loading = false;
      state.successfullyEndedActivity = true;
    });
    builder.addCase(endSearchActivity.rejected, (state) => {
      state.loading = false;
      state.hasErrors = true;
    });

    // Invitations
    builder.addCase(postInvitation.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(postInvitation.fulfilled, (state, { payload }) => {
      state.loading = false;
      const invitation = payload as Invitation;
      const agentId = invitation.agent;
      state.searchAgentInvitationMap = {
        ...state.searchAgentInvitationMap,
        [agentId]: invitation,
      };

      const newInvitationPayloads = { ...state.stagedInvitationPayloads };
      delete newInvitationPayloads[agentId];
      state.stagedInvitationPayloads = newInvitationPayloads;

      const exists =
        state.search.invitations.filter(
          (invitation) => invitation.id === payload.id,
        ).length > 0;
      if (!exists) {
        state.search.invitations = [...state.search.invitations, invitation];
      }

      if (state.invitationCreationErrors?.[agentId]) {
        const newInvitationCreationErrors = {
          ...state.invitationCreationErrors,
        };
        delete newInvitationCreationErrors[agentId];
        state.invitationCreationErrors = newInvitationCreationErrors;
      }
    });
    builder.addCase(postInvitation.rejected, (state, action) => {
      state.loading = false;
      const invitationError: InvitationCreationError =
        action.payload as InvitationCreationError;
      const { agentId } = invitationError;
      const newInvitationCreationErrors = { ...state.invitationCreationErrors };
      newInvitationCreationErrors[agentId] = invitationError;
      state.invitationCreationErrors = newInvitationCreationErrors;
    });
    builder.addCase(cancelInvitationById.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(cancelInvitationById.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.hasErrors = false;

      const { invitationId, agentId } = payload;
      const newInvitations = state.search.invitations.filter(
        (invitation) => invitation.id !== invitationId,
      );
      state.search.invitations = newInvitations;

      const newSearchAgentInvitationMap = { ...state.searchAgentInvitationMap };
      delete newSearchAgentInvitationMap[agentId];
      state.searchAgentInvitationMap = newSearchAgentInvitationMap;
    });
    builder.addCase(cancelInvitationById.rejected, (state) => {
      state.loading = false;
      state.hasErrors = true;
    });
    builder.addCase(getTemplates.fulfilled, (state, { payload }) => {
      state.templates = payload;
      if (!state.search?.id) {
        return;
      }

      try {
        const rebuiltInvitationPayloads = buildStagedInvitationPayloads(
          state.search,
          payload,
          state.searchAgentInvitationMap,
        );
        if (hasTemplateResolutionError(state)) {
          state.hasErrors = false;
          state.patchErrors = null;
          state.error = null;
        }
        state.stagedInvitationPayloads = {
          ...rebuiltInvitationPayloads,
          ...state.stagedInvitationPayloads,
        };
      } catch (error) {
        setTemplateResolutionError(state, error);
      }
    });
  },
});

export const {
  clearSearchId,
  clearSearchErrors,
  updateShowSearch,
  resetEndActivityState,
  stageAgentOnSearch,
  unstageAgentOnSearch,
  stageInvitationPayload,
  unstageInvitationPayload,
} = searchesSlice.actions;

// Reducer
export default searchesSlice.reducer;
